// src/pages/AttendanceEntry.jsx

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

import {
  getAttendanceStudents,
  getActiveTerms,
  saveAttendance,
} from "../api/services";

function AttendanceEntry() {
  const { t } = useTranslation();

  const { assignmentId } = useParams();

  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [subject, setSubject] = useState("");
  const [className, setClassName] = useState("");
  const [termId, setTermId] = useState(null);

  const [attendanceDate, setAttendanceDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [statuses, setStatuses] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {

    fetchData();

  }, []);

  const fetchData = async () => {

    try {

      const [studentsRes, termRes] =
        await Promise.all([

          getAttendanceStudents(
            assignmentId
          ),

          getActiveTerms(),
        ]);

      setStudents(
        studentsRes.data.students
      );

      setSubject(
        studentsRes.data.subject
      );

      setClassName(
        studentsRes.data.class
      );

      if (
        termRes.data.length > 0
      ) {

        setTermId(
          termRes.data[0].id
        );
      }

    } catch (err) {

      console.error(err);

      alert(
        "Failed to load attendance."
      );

    } finally {

      setLoading(false);
    }
  };

  const updateStatus = (
    studentId,
    status
  ) => {

    setStatuses((prev) => ({

      ...prev,

      [studentId]: status,
    }));
  };

  const handleSave = async () => {

    if (!termId) {

      alert(
        "No active term found."
      );

      return;
    }

    try {

      setSaving(true);

      await saveAttendance({

        assignment_id:
          assignmentId,

        attendance_date:
          attendanceDate,

        students:
          students.map((s) => ({

            student_id:
              s.id,

            status:
              statuses[s.id] ||
              "PRESENT",
          })),
      });

      alert(
        "Attendance saved successfully."
      );

    } catch (err) {

      console.error(err);

      alert(
        "Failed to save attendance."
      );

    } finally {

      setSaving(false);
    }
  };

  const completed =
    Object.keys(statuses).length;

  const progress =
    students.length === 0
      ? 0
      : Math.round(
          (
            completed /
            students.length
          ) * 100
        );

  if (loading) {

    return (
      <div style={{padding:"30px"}}>
        Loading attendance...
      </div>
    );
  }

  return (

    <div style={styles.page}>

      <div style={styles.header}>

        <div>

          <h2>
            {t("attendanceRegister")}
          </h2>

          <p>
            {subject}
            {" • "}
            {className}
          </p>

        </div>

        <button
          style={styles.backBtn}
          onClick={() =>
            navigate(
              "/teacher/my-assignments"
            )
          }
        >
          ← {t("back")}
        </button>

      </div>

      <div style={styles.dateBox}>

        <label>
          {t("attendanceDate")}
        </label>

        <input
          type="date"
          value={attendanceDate}
          onChange={(e) =>
            setAttendanceDate(
              e.target.value
            )
          }
          style={styles.dateInput}
        />

      </div>

      <div style={styles.tableWrapper}>

        <table style={styles.table}>

          <thead>

            <tr>

              <th>{t("student")}</th>

              <th>{t("present")}</th>

              <th>{t("absent")}</th>

              <th>Justified</th>

            </tr>

          </thead>

          <tbody>

            {students.map((student) => (

              <tr key={student.id}>

                <td>

                  {student.first_name}
                  {" "}
                  {student.last_name}

                </td>

                <td>

                  <input
                    type="radio"
                    name={`status-${student.id}`}
                    onChange={() =>
                      updateStatus(
                        student.id,
                        "PRESENT"
                      )
                    }
                  />

                </td>

                <td>

                  <input
                    type="radio"
                    name={`status-${student.id}`}
                    onChange={() =>
                      updateStatus(
                        student.id,
                        "ABSENT"
                      )
                    }
                  />

                </td>

                <td>

                  <input
                    type="radio"
                    name={`status-${student.id}`}
                    onChange={() =>
                      updateStatus(
                        student.id,
                        "JUSTIFIED"
                      )
                    }
                  />

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      <div style={styles.progressBox}>

        <div>
          Progress:
          {" "}
          {progress}%
        </div>

        <div style={styles.progressBar}>

          <div
            style={{
              ...styles.progressFill,
              width:`${progress}%`,
            }}
          />

        </div>

      </div>

      <button
        style={styles.saveBtn}
        onClick={handleSave}
        disabled={saving}
      >
        {
          saving
          ? "Saving..."
          : "Save Attendance"
        }
      </button>

    </div>
  );
}

const styles = {

  page:{
    padding:"6px",
    maxWidth:"1100px",
    margin:"0 auto",
    fontFamily:"Segoe UI, sans-serif",
  },

  header:{
    display:"flex",
    justifyContent:"space-between",
    alignItems:"center",
    marginBottom:"25px",
  },

  backBtn:{
    padding:"10px 14px",
    background:"#64748b",
    color:"white",
    border:"none",
    borderRadius:"8px",
    cursor:"pointer",
  },

  dateBox:{
    display:"flex",
    flexDirection:"column",
    gap:"8px",
    marginBottom:"20px",
  },

  dateInput:{
    width:"220px",
    padding:"10px",
  },

  tableWrapper:{
    overflowX:"auto",
    border:"1px solid #e2e8f0",
    borderRadius:"12px",
  },

  table:{
    width:"100%",
    borderCollapse:"collapse",
  },

  progressBox:{
    marginTop:"25px",
  },

  progressBar:{
    height:"10px",
    background:"#e2e8f0",
    borderRadius:"999px",
    overflow:"hidden",
    marginTop:"8px",
  },

  progressFill:{
    height:"100%",
    background:"#16a34a",
  },

  saveBtn:{
    marginTop:"25px",
    padding:"14px 22px",
    background:"#2563eb",
    color:"white",
    border:"none",
    borderRadius:"10px",
    fontWeight:"600",
    cursor:"pointer",
  },
};

export default AttendanceEntry;